import { formatDanishLongDate } from "@/util/date";
import { ReductionEvidenceListModel } from "../models/reduction";
import ClePagination from "./ClePagination";
import CleTable, { type CleTableColumn } from "./CleTable";
import ReportedStatusBadge from "./ReportedStatusBadge";
import { formatLessons } from "../utils/lessonLimits";

type ReductionsTableProps = {
  isError?: boolean;
  isLoading?: boolean;
  onReductionClick?: (reductionId: string) => void;
  pagination?: {
    isDisabled?: boolean;
    onPageChange: (page: number) => void;
    page: number;
    total: number;
    totalPages: number;
  };
  reductions: ReductionEvidenceListModel[];
  showStatusColumn?: boolean;
};

const ReductionsTable = ({
  isError = false,
  isLoading = false,
  onReductionClick,
  pagination,
  reductions,
  showStatusColumn = true,
}: ReductionsTableProps) => {
  const columns: CleTableColumn<ReductionEvidenceListModel>[] = [
    {
      id: "fileName",
      header: "Dokument",
      title: (reduction) => reduction.fileName ?? "",
      cell: (reduction) => reduction.fileName ?? "-",
    },
    {
      id: "createdAt",
      header: "Oprettet",
      cell: (reduction) => formatDanishLongDate(reduction.createdAt),
    },
    {
      id: "lessons",
      header: "Lektioner",
      className: "text-end",
      cell: (reduction) => formatLessons(reduction.numberOfLessons),
    },
    ...(showStatusColumn
      ? [
          {
            id: "status",
            header: "Status",
            cell: (reduction: ReductionEvidenceListModel) => (
              <ReportedStatusBadge reported={reduction.isReported} />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <CleTable
        columns={columns}
        data={reductions}
        emptyDescription="Når du opretter en reduktion, vises den her i oversigten."
        emptyTitle="Ingen reduktioner oprettet endnu"
        errorMessage="Reduktioner kunne ikke indlæses lige nu."
        isError={isError}
        isLoading={isLoading}
        getRowId={(reduction) => reduction.id}
        rowAction={
          onReductionClick
            ? {
                onClick: (id) => onReductionClick(id),
              }
            : undefined
        }
      />
      {pagination != null && reductions.length > 0 ? (
        <ClePagination
          ariaLabel="Reduktioner pagination"
          isDisabled={pagination.isDisabled}
          itemLabel="reduktioner"
          onPageChange={pagination.onPageChange}
          page={pagination.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
        />
      ) : null}
    </>
  );
};

export default ReductionsTable;
